/**
 * "Sistemi kim kullanıyor?" — Document 6 §9.2.
 *
 * The studio shares one login on a tablet, so this screen is what attributes
 * work to a person (Architecture §6.5). Targets are deliberately large: it is
 * used standing up, often with wet hands, between two clients.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import { useSession } from '../session/SessionContext.js';
import './InstructorSelect.css';

interface Instructor {
  id: string;
  name: string;
  profilePhotoUrl: string | null;
  status: 'active' | 'inactive';
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase('tr-TR') ?? '')
    .join('');
}

export function InstructorSelect() {
  const { status, session, setActiveInstructor, refresh, signOut } = useSession();
  const navigate = useNavigate();

  const [instructors, setInstructors] = useState<Instructor[] | null>(null);
  const [error, setError] = useState<string>();
  const [pendingId, setPendingId] = useState<string>();
  const [prompting, setPrompting] = useState<Instructor | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [managerPassword, setManagerPassword] = useState('');
  const [managerBusy, setManagerBusy] = useState(false);
  const [password, setPassword] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ items: Instructor[] }>('/api/instructors');
      setInstructors(data.items);
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
      setInstructors([]);
    }
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    void load();

    /*
     * Arriving here means nobody is working, so the session gives up whatever
     * it was holding. Without this the tablet could sit on this screen with an
     * unlocked studio behind it, and the next person to walk up would inherit
     * it by pressing Back.
     */
    void api
      .post('/api/auth/leave-instructor')
      .then(() => refresh())
      .catch(() => {
        // An instructor session has nothing to give up; it stays as it is.
      });
  }, [status, load, refresh]);

  if (status === 'loading') return <main className="picker" aria-busy="true" />;
  if (status === 'anonymous') return <Navigate to="/giris" replace />;

  /**
   * The studio password opened the session; it does not open a person. From
   * the moment a profile is entered the app shows that instructor's students
   * and attributes their work, so entering one costs their own password —
   * unless the studio switched that off in Ayarlar, in which case this is
   * attribution only and `pw` is never sent.
   */
  const skipProfilePassword = session?.skipProfilePassword ?? false;

  async function enterProfile(instructor: Instructor, pw?: string) {
    setPendingId(instructor.id);
    setError(undefined);
    try {
      await api.post<{ activeInstructor: { id: string; name: string } }>(
        '/api/auth/select-instructor',
        pw === undefined
          ? { instructorId: instructor.id }
          : { instructorId: instructor.id, password: pw },
      );
      setActiveInstructor({ id: instructor.id, name: instructor.name });
      await refresh();
      setPrompting(null);
      setPassword('');
      navigate('/panel', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
      setPassword('');
      setPendingId(undefined);
    }
  }

  /**
   * Manager mode is bought, not fallen back into.
   *
   * The tablet keeps the studio's session open behind this screen. Without the
   * password here, an instructor could step out of their own profile and tap
   * straight through to every student in the studio. A studio that switched
   * `skipProfilePassword` on has already decided that trade for itself.
   */
  const isEmpty = instructors !== null && instructors.length === 0;

  async function enterManager(pw?: string) {
    setManagerBusy(true);
    setError(undefined);
    try {
      await api.post('/api/auth/enter-manager', pw === undefined ? {} : { password: pw });
      await refresh();
      // A studio with nobody in it has one job waiting, so go straight to it.
      navigate(isEmpty ? '/egitmenler' : '/panel', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
      setManagerPassword('');
    } finally {
      setManagerBusy(false);
    }
  }

  return (
    <main className="picker">
      <header className="picker__header">
        <p className="picker__studio">{session?.studio.name}</p>
        <h1 className="picker__title">Sistemi kim kullanıyor?</h1>
      </header>

      {error && (
        <p className="picker__error" role="alert">
          {error}
        </p>
      )}

      {instructors === null && <p className="picker__loading">Yükleniyor…</p>}

      {isEmpty && (
        <div className="picker__empty">
          <p>Henüz eğitmen eklenmemiş.</p>
          <p className="picker__hint">
            Her eğitmen kendi kullanıcı adı ve şifresiyle girer, bu yüzden hesap
            oluşturmak Eğitmenler ekranından yapılır.
          </p>
          <Button
            type="button"
            onClick={() => {
              if (skipProfilePassword) void enterManager();
              else setManagerOpen(true);
            }}
          >
            Yönetici Olarak Gir
          </Button>
        </div>
      )}

      {instructors !== null && instructors.length > 0 && (
        <>
          <ul className="picker__grid">
            {instructors.map((instructor) => (
              <li key={instructor.id}>
                <button
                  type="button"
                  className="picker__card"
                  onClick={() => {
                    setError(undefined);
                    if (skipProfilePassword) {
                      void enterProfile(instructor);
                      return;
                    }
                    setPassword('');
                    setManagerOpen(false);
                    setPrompting(instructor);
                  }}
                  disabled={pendingId !== undefined}
                  aria-busy={pendingId === instructor.id || undefined}
                >
                  <span className="picker__avatar" aria-hidden="true">
                    {initials(instructor.name)}
                  </span>
                  <span className="picker__name">{instructor.name}</span>
                </button>
              </li>
            ))}
          </ul>

          {prompting && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void enterProfile(prompting, password);
              }}
              className="picker__signin"
            >
              <p className="picker__signin-who">
                <b>{prompting.name}</b> olarak devam
              </p>
              <Field
                label="Şifreniz"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
              <div className="picker__signin-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setPrompting(null);
                    setPassword('');
                    setError(undefined);
                  }}
                >
                  Vazgeç
                </Button>
                <Button
                  type="submit"
                  loading={pendingId === prompting.id}
                  disabled={password === ''}
                >
                  Gir
                </Button>
              </div>
            </form>
          )}

          <div className="picker__actions">
            {/* An owner who does not teach should not have to pretend to be
                one of their own instructors to reach the app. An instructor
                who signed in as themselves has no such mode to fall back to. */}
            {session?.role === 'owner' && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setError(undefined);
                  setPrompting(null);
                  if (skipProfilePassword) void enterManager();
                  else setManagerOpen(true);
                }}
              >
                Yönetici olarak devam et
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => void signOut()}>
              Çıkış Yap
            </Button>
          </div>
        </>
      )}

      {/*
       * A sibling of both branches above, not nested in either — it used to
       * live inside the "there are instructors" branch, which meant a brand
       * new studio with none yet could never open it: the button that sets
       * managerOpen was reachable, the form that reads it was not, and the
       * owner of every fresh studio was locked out of their own app before
       * they could add the first instructor. Found by hand, testing the
       * exact studio a real signup starts as, on 10 September 2026.
       */}
      {managerOpen && (
        <form
          className="picker__signin"
          onSubmit={(event) => {
            event.preventDefault();
            void enterManager(managerPassword);
          }}
        >
          <p className="picker__signin-who">
            <b>Yönetici</b> olarak devam
          </p>
          <Field
            label="Salon şifresi"
            type="password"
            autoComplete="current-password"
            value={managerPassword}
            onChange={(e) => setManagerPassword(e.target.value)}
            required
            autoFocus
          />
          <div className="picker__signin-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setManagerOpen(false);
                setManagerPassword('');
                setError(undefined);
              }}
            >
              Vazgeç
            </Button>
            <Button type="submit" loading={managerBusy} disabled={managerPassword === ''}>
              Gir
            </Button>
          </div>
        </form>
      )}
    </main>
  );
}
